import {
    COMPARISON,
    DATA_STATE,
    DEFAULT_SEARCH_TYPE,
    MAX_PERIOD_DAYS,
    SEARCH_TYPES,
    inclusiveDays,
    resolveBaseline,
} from './period';

/**
 * Turns the draft the scope controls hold into the scope a request is built from, or reports why
 * it cannot be.
 *
 * Dates are never silently shortened: a window past the product bound is an error the user has to
 * resolve, not something a report quietly trims. Unequal windows are allowed but stated, because
 * day-by-day alignment and totals mean different things across them.
 *
 * Every GSC tool resolves its scope through this so that two reports cannot disagree about what
 * the same draft means. A report with further controls of its own adds its fields and its own
 * errors to what comes back rather than re-validating any of this.
 */
export function resolveScope(draft, t, { maxDays = MAX_PERIOD_DAYS } = {}) {
    const errors = [];
    const warnings = [];

    const current = { startDate: draft.startDate, endDate: draft.endDate };
    const currentDays = inclusiveDays(current);

    if (!currentDays) {
        errors.push(t('tools.gsc.errors.invalid_period'));
    } else if (currentDays > maxDays) {
        errors.push(t('tools.gsc.errors.period_too_long', { days: maxDays }));
    }

    const baseline = resolveBaseline(current, draft.comparison, draft.customBaseline);
    const baselineDays = inclusiveDays(baseline);

    if (draft.comparison === COMPARISON.CUSTOM && !baselineDays) {
        errors.push(t('tools.gsc.errors.invalid_baseline'));
    } else if (baseline && baselineDays > maxDays) {
        errors.push(t('tools.gsc.errors.baseline_too_long', { days: maxDays }));
    } else if (baseline && baselineDays !== currentDays) {
        warnings.push(
            t('tools.gsc.warnings.unequal_periods', {
                current: currentDays,
                baseline: baselineDays,
            }),
        );
    }

    if (!draft.siteUrl) {
        errors.push(t('tools.gsc.errors.no_property'));
    }

    return {
        siteUrl: draft.siteUrl,
        permissionLevel: draft.permissionLevel,
        searchType: SEARCH_TYPES.includes(draft.searchType)
            ? draft.searchType
            : DEFAULT_SEARCH_TYPE,
        current,
        baseline,
        comparison: draft.comparison,
        country: draft.country,
        device: draft.device,
        dataState: draft.includePreliminary ? DATA_STATE.ALL : DATA_STATE.FINAL,
        currentDays,
        baselineDays,
        errors,
        warnings,
    };
}

/** The draft every scope control panel starts from, before a report adds its own fields. */
export function emptyDraft(period) {
    return {
        siteUrl: '',
        permissionLevel: '',
        searchType: DEFAULT_SEARCH_TYPE,
        country: '',
        device: '',
        startDate: period.startDate,
        endDate: period.endDate,
        comparison: COMPARISON.PREVIOUS,
        customBaseline: { startDate: '', endDate: '' },
        includePreliminary: false,
    };
}
